import React, { HTMLAttributes, useEffect, useState } from "react";
import { useFormik } from "formik";
import {
  TextField,
  Button,
  Grid,
  FormControlLabel,
  Switch,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Paper,
} from "@mui/material";
import * as Yup from "yup";
import { uploadAvatar } from "../../services/images";
import { Teacher } from "../../types/TeacherModel";
import { closeSnackbar, enqueueSnackbar } from "notistack";
import EntityAutocomplete from "../EntityAutocomplete";
import { School } from "../../types/School";
import CloseIcon from "@mui/icons-material/Close";
import AvatarUpload from "../AvatarUpload";

export interface TeacherFormProps {
  isCombinedForm?: boolean;
  selectedSchool?: School;
  schools: School[];
  selectedRow?: Teacher;
  onSubmit: (values: Teacher) => Promise<void>;
}

export const TeacherForm: React.FC<TeacherFormProps> = ({
  isCombinedForm,
  selectedSchool,
  schools,
  selectedRow,
  onSubmit,
}) => {
  const formik = useFormik({
    initialValues: {
      photoUrl: "",
      status: "Pending Registration",
      name: "",
      city: "",
      country: "",
      schoolId: null,
      schoolName: null,
      email: "",
      mobile: "",
      isActive: false,
      ...selectedRow,
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      name: Yup.string()
        .max(100, "Must be 100 characters or less")
        .required("Required"),
      city: Yup.string().max(50, "Must be 50 characters or less").optional(),
      country: Yup.string()
        .max(50, "Must be 50 characters or less")
        .required("Required"),
      email: Yup.string().email("Invalid email format").required("Required"),
      mobile: Yup.string(),
      isActive: Yup.boolean(),
    }),
    onSubmit: async (values: Teacher) => {
      let submissionValues = { ...values };

      // Remove schoolName and schoolId if they are null or ""
      if (!submissionValues.schoolId) {
        delete submissionValues.schoolName;
        delete submissionValues.schoolId;
      }
      await onSubmit(submissionValues);
      setTimeout(() => {
        formik.resetForm({ values: submissionValues });
      }, 0);
    },
  });

  const [openDialog, setOpenDialog] = useState(false);

  const shouldDisplaySchoolOptions = isCombinedForm
    ? false
    : !formik.values.schoolId;

  useEffect(() => {
    if (selectedSchool) {
      formik.setFieldValue("schoolId", selectedSchool?._id || null);
      formik.setFieldValue("schoolName", selectedSchool?.name || null);
      formik.setFieldValue("isActive", selectedSchool?.isActive);
    }
  }, [selectedSchool]);

  const handleAvatarUpload = (file: File) => {
    uploadAvatar(file)
      .then((response) => {
        formik.setFieldValue("photoUrl", response.imageUrl);
      })
      .catch((error) => {
        enqueueSnackbar(`${error?.response?.data}`, {
          variant: "error",
          action: (key) => (
            <Button onClick={() => closeSnackbar(key)}>Dismiss</Button>
          ),
        });
      });
  };

  const handleAvatarRemove = () => {
    formik.setFieldValue("photoUrl", "");
  };

  const handleSchoolSelect = (selectedSchool: School) => {
    formik.setFieldValue("schoolId", selectedSchool?._id || "");
    formik.setFieldValue("schoolName", selectedSchool?.name || "");
  };

  const getSchoolAutocompleteDisplay = (
    props: HTMLAttributes<HTMLElement>,
    school: School
  ) => {
    return (
      <li key={school._id} {...props}>
        {school.name} - {school.city}, {school.country}
      </li>
    );
  };

  //when edit
  const handleSchoolRemoveClick = () => {
    setOpenDialog(false);
    formik.setFieldValue("schoolId", null);
    formik.setFieldValue("schoolName", null);
  };

  const shouldDisableSubmit = (): boolean => {
    if (isCombinedForm) {
      return !formik.values.schoolId || !formik.dirty;
    }

    return !formik.dirty;
  };

  return (
    <>
      <form onSubmit={formik.handleSubmit}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <AvatarUpload
              photoUrl={formik.values.photoUrl}
              logoText="Teacher"
              onSubmit={(file: File) => handleAvatarUpload(file)}
              onRemove={() => handleAvatarRemove()}
            ></AvatarUpload>
          </Grid>

          {/* Name Field */}
          <Grid item xs={12}>
            <TextField
              size="small"
              fullWidth
              autoComplete="off"
              id="name"
              name="name"
              label="Name"
              value={formik.values.name}
              onChange={formik.handleChange}
              error={formik.touched.name && Boolean(formik.errors.name)}
              helperText={formik.touched.name && formik.errors.name}
            />
          </Grid>

          {/* City Field */}
          <Grid item xs={12} md={6}>
            <TextField
              size="small"
              fullWidth
              autoComplete="off"
              id="city"
              name="city"
              label="City"
              value={formik.values.city}
              onChange={formik.handleChange}
              error={formik.touched.city && Boolean(formik.errors.city)}
              helperText={formik.touched.city && formik.errors.city}
            />
          </Grid>

          {/* Country Field */}
          <Grid item xs={12} md={6}>
            <TextField
              size="small"
              fullWidth
              autoComplete="off"
              id="country"
              name="country"
              label="Country"
              value={formik.values.country}
              onChange={formik.handleChange}
              error={formik.touched.country && Boolean(formik.errors.country)}
              helperText={formik.touched.country && formik.errors.country}
            />
          </Grid>

          {/* Email Field */}
          <Grid item xs={12}>
            <TextField
              size="small"
              fullWidth
              autoComplete="off"
              id="email"
              name="email"
              label="Email"
              value={formik.values.email}
              onChange={formik.handleChange}
              error={formik.touched.email && Boolean(formik.errors.email)}
              helperText={formik.touched.email && formik.errors.email}
            />
          </Grid>

          {/* Mobile Field */}
          <Grid item xs={12}>
            <TextField
              size="small"
              fullWidth
              autoComplete="off"
              id="mobile"
              name="mobile"
              label="Mobile"
              value={formik.values.mobile}
              onChange={formik.handleChange}
              error={formik.touched.mobile && Boolean(formik.errors.mobile)}
              helperText={formik.touched.mobile && formik.errors.mobile}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  color="primary"
                  checked={formik.values.isActive}
                  onChange={(event) => {
                    formik.setFieldValue("isActive", event.target.checked);
                  }}
                />
              }
              label={formik.values.isActive ? "Active" : "Disabled"}
              labelPlacement="start"
            />
          </Grid>
          <Grid item xs={12}>
            {shouldDisplaySchoolOptions ? (
              <EntityAutocomplete
                entityName="School"
                entityOptions={schools}
                onEntitySelect={handleSchoolSelect}
                renderDisplay={(props, school) =>
                  getSchoolAutocompleteDisplay(props, school)
                }
              />
            ) : (
              <Paper
                elevation={1}
                style={{
                  padding: "10px",
                  margin: "10px 0",
                  display: "flex",
                  alignItems: "center",
                  backgroundColor: "#e3f2fd",
                }}
              >
                <span style={{ fontWeight: "bold", marginRight: "10px" }}>
                  Assigned school:
                </span>
                <span style={{ flex: 1 }}>{formik.values.schoolName}</span>
                {!selectedSchool && !isCombinedForm && (
                  <IconButton onClick={() => setOpenDialog(true)}>
                    <CloseIcon />
                  </IconButton>
                )}
              </Paper>
            )}
          </Grid>
        </Grid>
        <Button
          color="primary"
          variant="contained"
          type="submit"
          disabled={shouldDisableSubmit()}
          style={{ marginTop: "2rem" }}
        >
          {formik.values._id ? "Update" : "Add new"} teacher
        </Button>
      </form>

      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Remove School</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to remove the selected school -{" "}
            {selectedRow?.schoolName}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} color="primary">
            Cancel
          </Button>
          <Button
            onClick={() => {
              setOpenDialog(false);
              handleSchoolRemoveClick();
            }}
            color="primary"
            autoFocus
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TeacherForm;
